<template>
  <header class="header">
    <div class="breadcrumbs">
      <el-scrollbar>
        <div class="data">
          <template
            v-for="(breadcrumb, index) in mappedBreadcrumbs"
            :key="index"
          >
            <!-- Separator -->
            <svg
              v-if="index > 0"
              class="separator-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>

            <div
              @click="breadcrumb.to && $router.push(breadcrumb.to)"
              :class="['item', isLastItem(index) ? 'item-active' : '']"
            >
              <div class="text">
                {{ breadcrumb.title }}
              </div>
            </div>
          </template>
        </div>
      </el-scrollbar>
    </div>
    <div class="shrink-0 ml-2 flex items-center gap-3">
      <NotificationsBell />
      <ThemeToggle :show-advanced="true" />
      <LangSwitch></LangSwitch>
    </div>
  </header>
</template>

<script>
import { LanguageSwitch as LangSwitch } from "@/features/language-switch";
import ThemeToggle from "@/shared/ui/theme-toggle/ThemeToggle.vue";
import { NotificationsBell } from "@/features/notifications-bell";

export default {
  name: "LayoutHeader",
  components: {
    LangSwitch,
    ThemeToggle,
    NotificationsBell,
  },

  props: {
    rootRouteName: {
      type: String,
      default: "default",
    },
    navbarSourceRoute: {
      type: Object,
      default: null,
    },
    activePageTitle: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      breadcrumbs: [],
    };
  },

  computed: {
    mappedBreadcrumbs() {
      if (!this.activePageTitle) return this.breadcrumbs;

      return [
        ...this.breadcrumbs,
        {
          title: this.activePageTitle,
          icon: null,
          to: null,
        },
      ];
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler(to) {
        this.getBreadcrumbs(to);
      },
    },
  },

  methods: {
    isLastItem(index) {
      return (
        index === this.mappedBreadcrumbs.length - 1 &&
        this.mappedBreadcrumbs.length > 1
      );
    },
    openPage(to) {
      this.$router.push(to);
    },

    back() {
      if (this.breadcrumbs.length < 2) return;

      const prev = this.breadcrumbs[this.breadcrumbs.length - 2];
      if (prev && prev.to) {
        this.$router.push(prev.to);
      }
    },

    getBreadcrumbs(route) {
      this.breadcrumbs = [];
      const matchedRoutes = route.matched;

      matchedRoutes.forEach((route) => {
        if (route.name && route.meta.isDashboardItem) {
          const breadcrumbItem = {
            title: route.meta.title,
            name: route.name,
            to: { name: route.name },
            icon: route.meta.icon || null,
          };

          if (route.meta.isSidebarItem) {
            let rootRoute = {
              name: "root",
              children: this.$router.options.routes,
            };

            const pageChildren = this.findChildrenByName(rootRoute, route.name);

            if (pageChildren && pageChildren.length > 0) {
              breadcrumbItem.children = pageChildren
                .filter((p) => p.meta && p.meta.isDashboardItem)
                .map((c) => ({
                  title: (c.meta && c.meta.title) || c.name,
                  name: c.name,
                  to: { name: c.name },
                  icon: c.meta.icon,
                  meta: { ...c.meta },
                }));
            } else {
              breadcrumbItem.children = [];
            }
          }

          this.breadcrumbs.push(breadcrumbItem);
        }
      });
    },

    findRoute(route, name) {
      if (route.name === name) {
        return route || {};
      } else if (route.children) {
        for (const childRoute of route.children) {
          const result = this.findRoute(childRoute, name);
          if (result.name) {
            return result;
          }
        }
      }
      return {};
    },

    findChildrenByName(route, name) {
      if (route.name === name) {
        return route.children || [];
      } else if (route.children) {
        for (const childRoute of route.children) {
          const result = this.findChildrenByName(childRoute, name);
          if (result.length > 0) {
            return result;
          }
        }
      }
      return [];
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  @apply flex justify-between items-center;
}

.breadcrumbs {
  @apply flex items-center gap-1 overflow-hidden flex-1;
}

.back {
  @apply rounded-md flex justify-center items-center cursor-pointer transition-colors text-muted-foreground;
  &:hover {
    @apply text-foreground;
  }
}

.data {
  @apply flex flex-wrap items-center gap-1.5 whitespace-nowrap w-full text-sm text-muted-foreground;
}

.item {
  @apply inline-flex items-center gap-1.5 cursor-pointer transition-colors text-muted-foreground;
  &:hover {
    @apply text-foreground;
  }
}

.item-active {
  @apply font-normal text-foreground;
  cursor: default;
  &:hover {
    @apply text-foreground;
  }
}

.text {
  @apply text-sm leading-5;
}

.separator-icon {
  @apply w-3.5 h-3.5 shrink-0 text-muted-foreground;
}
</style>
